/**
 * @fileoverview Syndicator Showdown — Admin Firestore Service
 * @module services/adminFirestore
 */

import {
  doc,
  collection,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  writeBatch,
  serverTimestamp,
  query,
  orderBy,
  where,
} from 'firebase/firestore';

import { db } from './firebase';

// ─── Slate ID Generation ──────────────────────────────────────────────────────

function generateSlateId(meetingDate, gameName) {
  const dateStr  = meetingDate.replace(/-/g, '');
  const nameStr  = gameName
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
    .substring(0, 30);
  return `${dateStr}_${nameStr}`;
}

// ─── Admin Defaults ───────────────────────────────────────────────────────────

export async function loadAdminDefaults(adminId) {
  const userDoc = await getDoc(doc(db, 'users', adminId));
  if (!userDoc.exists()) return null;
  return userDoc.data().adminDefaults || null;
}

export async function saveAdminDefaults(adminId, defaults) {
  await updateDoc(doc(db, 'users', adminId), {
    adminDefaults: {
      ...defaults,
      savedAt: serverTimestamp(),
    },
  });
}

// ─── Available Races (from scraper) ──────────────────────────────────────────

/**
 * Normalises a raw runner document from the meetings/{id}/runners subcollection
 * into the shape the admin UI and game_slates expect.
 *
 * The Birdcage scraper uses different field names than the admin panel:
 *   horseName   → name
 *   horseNumber → runnerNumber
 *
 * All other fields (barrier, jockey, trainer, status, raceNumber, meetingKey,
 * startTime, racingCode, source, venue, state) are passed through or discarded.
 */
/**
 * Computes the correct UTC millisecond instant from a venue-local "HH:MM" time
 * and the state/timezone. Uses the same Intl-probe algorithm as localTimeToUtcDate
 * in upload_racing_data.js — safe to run in the browser.
 *
 * WHY this exists: the Birdcage scraper's coerceToTimestamp() stored startTimestamp
 * on runner documents treating local "HH:MM" as UTC (e.g. WA 15:30 → 15:30 UTC
 * instead of the correct 07:30 UTC). This function recomputes from the raw
 * startTime + state and bypasses those wrong stored values.
 *
 * Returns UTC millis (number), or null if the input cannot be resolved.
 */
const IANA_BY_STATE = {
  NSW: 'Australia/Sydney', VIC: 'Australia/Sydney', TAS: 'Australia/Sydney',
  ACT: 'Australia/Sydney', QLD: 'Australia/Brisbane', SA:  'Australia/Adelaide',
  NT:  'Australia/Darwin', WA:  'Australia/Perth',
};

function computeCorrectStartMs(dateOnly, timeStr, state) {
  if (!dateOnly || !timeStr) return null;
  const tz  = IANA_BY_STATE[state] || 'Australia/Brisbane';
  const tm  = String(timeStr).match(/^(\d{1,2}):(\d{2})/);
  if (!tm) return null;
  const hh = parseInt(tm[1], 10);
  const mm = parseInt(tm[2], 10);
  if (hh > 23 || mm > 59) return null;

  const y  = parseInt(dateOnly.slice(0, 4), 10);
  const mo = parseInt(dateOnly.slice(4, 6), 10) - 1;
  const dd = parseInt(dateOnly.slice(6, 8), 10);

  try {
    // Step 1: treat local HH:MM as if UTC (probe value)
    const naiveUtcMs = Date.UTC(y, mo, dd, hh, mm, 0);

    // Step 2: render probe in the target timezone → what local time does it show?
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).formatToParts(new Date(naiveUtcMs));
    const get  = type => parseInt(parts.find(p => p.type === type)?.value ?? '0', 10);
    const localInterpMs = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));

    // Step 3: offset = what the probe *looks like* in that timezone - what it actually is
    const offsetMs = localInterpMs - naiveUtcMs;

    // Step 4: subtract offset to get the true UTC instant
    return naiveUtcMs - offsetMs;
  } catch {
    return null;
  }
}

function normaliseRunner(runnerDoc) {
  const d = runnerDoc.data();
  return {
    // Use horseName as primary; fall back to runnerName or the doc ID
    id:           d.horseName || d.runnerName || runnerDoc.id,
    name:         d.horseName || d.runnerName || runnerDoc.id,
    runnerNumber: d.horseNumber   ?? d.runnerNumber ?? null,
    horseNumberLabel: d.horseNumberLabel ?? null,
    barrier:      d.barrier       ?? null,
    jockey:       d.jockey        || '',
    trainer:      d.trainer       || '',
    status:       d.status        || 'Active',
    // raceNumber is essential for grouping — must exist on every runner doc
    raceNumber:   d.raceNumber    ?? null,
    // startTimestamp: timezone-correct Firestore Timestamp for the race jump time.
    // All runners in the same race share the same startTimestamp — it is promoted
    // to the race-level startTimestamp in fetchAvailableRaces if not already set.
    startTimestamp: d.startTimestamp || null,

    // ── Rich form/career data scraped from the Racing Australia Form Guide ──
    // Passed straight through into game_slates/{id}/races/{id}.runners[] so the
    // v4 rating engine (mapRunnerToV4 / buildRatingInputs) actually receives
    // it — previously these were stripped here, starving the engine of data
    // and causing every runner to be scored as a near-debutant.
    age:                 d.age ?? null,
    sex:                 d.sex ?? null,
    weight:              d.weight ?? null,
    apprenticeClaim:     d.apprenticeClaim ?? null,
    career:              d.career ?? null,
    prizemoney:          d.prizemoney ?? null,
    averagePrizeMoneyPerStart: d.averagePrizeMoneyPerStart ?? null,
    firstUp:             d.firstUp ?? null,
    secondUp:            d.secondUp ?? null,
    trackRecord:         d.trackRecord ?? null,
    distanceRecord:      d.distanceRecord ?? null,
    trackDistanceRecord: d.trackDistanceRecord ?? null,
    distancesWon:        d.distancesWon ?? null,
    goingRecordBySurface: d.goingRecordBySurface ?? null,
    formLines:           d.formLines ?? [],
  };
}

/**
 * Fetches race meetings and their runners from the `meetings` collection
 * written by the Birdcage scraper, for a specific calendar date.
 *
 * Data shape in Firestore:
 *   meetings/{meetingKey}
 *     .date        "2026Jun08"
 *     .raceCount   8
 *     .races       { "1": { distance, startTime }, "2": { ... }, ... }
 *     .venue       "Mornington"   (may also be in doc ID)
 *     .state       "VIC"
 *
 *   meetings/{meetingKey}/runners/{horseName}
 *     .horseName   "ABSENT"
 *     .horseNumber 1
 *     .raceNumber  2
 *     .barrier     2
 *     .jockey      "Beau Mertens"
 *     .trainer     "Greg Eurell"
 *     .status      "Active"
 *     ... etc.
 *
 * @param {string} meetingDate - "2026-06-08" OR already-formatted "2026Jun08"
 * @returns {Promise<Array>} Array of meeting objects with embedded runners per race
 */
export async function fetchAvailableRaces(meetingDate) {
  if (!meetingDate) return [];

  // Convert "2026-06-08" → "2026Jun08" to match the scraper's date field format.
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  let queryDate = meetingDate;
  if (meetingDate.includes('-')) {
    const parts = meetingDate.split('-');
    if (parts.length === 3) {
      const [year, monthPart, day] = parts;
      const mon = MONTHS[parseInt(monthPart, 10) - 1];
      if (mon) queryDate = `${year}${mon}${day}`;
    }
  }

  try {
    const snapshot = await getDocs(
      query(collection(db, 'meetings'), where('date', '==', queryDate))
    );

    if (snapshot.empty) return [];

    // Fetch all meetings in parallel — each needs a runners subcollection fetch
    const meetingPromises = snapshot.docs.map(async (meetingDoc) => {
      const data = meetingDoc.data();

      // ── 0. Derive state + dateOnly early (needed for startTimestamp computation)
      const idParts    = meetingDoc.id.split('_');
      const dateOnly   = idParts[0];                                     // "20260613"
      const stateEarly = data.state || data.meetingState || idParts[1] || 'QLD';

      // ── 1. Build the races array from the races map ──────────────────────
      // Scraper stores: races: { "1": { distance, startTime }, "2": {...} }
      // startTimestamp on runner docs was stored wrong (coerceToTimestamp treated
      // local "HH:MM" as UTC). We recompute from startTime + state using Intl —
      // this is timezone-correct and works for all states including WA.
      let racesMap = {};
      if (data.races && typeof data.races === 'object' && !Array.isArray(data.races)) {
        Object.entries(data.races).forEach(([raceNumStr, raceDetails]) => {
          const raceNumber      = parseInt(raceNumStr, 10);
          const startTimestampMs = computeCorrectStartMs(dateOnly, raceDetails.startTime, stateEarly);
          racesMap[raceNumber] = {
            raceNumber,
            // Real race name/class/prizemoney now come from the Racing Australia
            // Form Guide scrape (birdcage_master._tbScrape) — fall back to the
            // generic "Race N" / 'Open' only when the scraper didn't capture them
            // (e.g. non-TB codes, or a meeting scraped before this field existed).
            raceName:       raceDetails.raceName || `Race ${raceNumber}`,
            classStr:       raceDetails.classStr || null,
            raceClass:      raceDetails.raceClass || null,
            racePrizemoney: raceDetails.racePrizemoney ?? null,
            going:          raceDetails.going || data.trackRating || null,
            distance:       raceDetails.distance || 0,
            scheduledJump:  raceDetails.startTime || '',
            // UTC millis computed correctly via Intl — safe to compare with Date.now()
            startTimestamp: startTimestampMs,
            runners:        [],   // will be populated from subcollection below
            id:             `${meetingDoc.id}_R${raceNumber}`,
          };
        });
      }

      // ── 2. Fetch all runner documents from the subcollection ─────────────
      try {
        const runnersSnap = await getDocs(
          collection(db, 'meetings', meetingDoc.id, 'runners')
        );

        runnersSnap.docs.forEach(runnerDoc => {
          const runner  = normaliseRunner(runnerDoc);
          const raceNum = runner.raceNumber;

          if (raceNum && racesMap[raceNum]) {
            // Runner belongs to a known race — embed it.
            // startTimestamp is already set from the race-level computation above;
            // do NOT promote the runner's stored startTimestamp (it's wrong for
            // interstate venues due to the coerceToTimestamp UTC-treat bug).
            racesMap[raceNum].runners.push(runner);
          } else if (raceNum) {
            // Runner references a race not in the races map — create a stub
            racesMap[raceNum] = {
              raceNumber:    raceNum,
              raceName:      `Race ${raceNum}`,
              distance:      0,
              scheduledJump: '',
              startTimestamp: null, // no race-level startTime available for stub races
              runners:       [runner],
              id:            `${meetingDoc.id}_R${raceNum}`,
            };
          }
          // If raceNumber is null/missing we skip — can't place the runner
        });
      } catch (runnerErr) {
        // Subcollection fetch failed — log and continue with empty runners
        console.warn(`Failed to fetch runners for ${meetingDoc.id}:`, runnerErr);
      }

      // ── 3. Sort runners within each race by runnerNumber ─────────────────
      Object.values(racesMap).forEach(race => {
        race.runners.sort((a, b) => (a.runnerNumber ?? 999) - (b.runnerNumber ?? 999));
      });

      // ── 4. Convert race map to sorted array ──────────────────────────────
      const racesArray = Object.values(racesMap)
        .sort((a, b) => a.raceNumber - b.raceNumber);

      // ── 5. Extract venue from doc ID as a reliable fallback ─────────────
      // e.g. "20260608_VIC_Mornington_TB" → state="VIC", venue="Mornington"
      // (idParts and stateEarly already derived in step 0 above)
      const venueFallback = idParts.length >= 3
        ? idParts.slice(2, -1).join(' ')
        : '';

      return {
        id:           meetingDoc.id,
        meetingName:  data.venue      || data.meetingName  || venueFallback,
        meetingState: stateEarly,
        code:         data.racingCode || data.code         || 'TB',
        races:        racesArray,
      };
    });

    return await Promise.all(meetingPromises);

  } catch (err) {
    console.error('fetchAvailableRaces error:', err);
    return [];
  }
}

// ─── Slate Creation ───────────────────────────────────────────────────────────

export async function createSlateDraft(config, adminId) {
  const {
    gameName,
    tournamentId,
    meetingDate,
    selectedRaces,
    avgSpendPerRace,
    minSpendPerRace,
    defaultPrizePool,
    prizeDescription,
    featureRaceOverrides,
    priceLockTime,
    entriesOpenMode,
    entriesOpenAt,
    entryLockMode,
    qualifiersPerSlate,
  } = config;

  const slateId  = generateSlateId(meetingDate, gameName);
  const slateRef = doc(db, 'game_slates', slateId);

  const existing = await getDoc(slateRef);
  if (existing.exists()) {
    throw new Error(
      `A slate already exists for "${gameName}" on ${meetingDate}. ` +
      `Please use a different game name or edit the existing slate.`
    );
  }

  const totalRaces = selectedRaces.length;
  const perRaceFloor = minSpendPerRace;
  const batch = writeBatch(db);

  batch.set(slateRef, {
    id:                slateId,
    title:             gameName,
    tournamentId:      tournamentId || null,
    meetingDate,

    // Game economics
    totalRaces,
    avgSpendPerRace,
    bankrollPerPlayer:  avgSpendPerRace * totalRaces,
    perRaceFloor,

    // Entry fee configuration mappings
    entryFeeEnabled:       config.entryFeeEnabled       || false,
    entryFeeAmount:        config.entryFeeAmount         || 0,
    houseTakePercent:      config.houseTakePercent        || 0,
    prizeType:             config.prizeType               || 'pre_funded',
    preFundedPrizeAmount:  config.preFundedPrizeAmount    || 0,
    prizeDistribution:     config.prizeDistribution       || [],
    grossPrizePool:        0,   
    netPrizePool:          0,   
    totalEntriesPaid:      0,   

    // Prize structure
    defaultPrizePool,
    prizeDescription:  prizeDescription || '',
    featureRaceOverrides: featureRaceOverrides || {},

    // Timing
    priceLockTime:     priceLockTime ? new Date(priceLockTime) : null,
    firstRaceJump:     null,  
    entriesOpenMode,
    entriesOpenAt:     entriesOpenAt ? new Date(entriesOpenAt) : null,
    entryLockMode,

    // Tournament
    qualifiersPerSlate: qualifiersPerSlate || 3,

    // State flags
    pricesLocked:  false,
    entriesOpen:   false,
    status:        'building',
    dataSource:    'admin_manual',

    // Metadata
    createdAt:     serverTimestamp(),
    createdBy:     adminId,
    lastModifiedAt: serverTimestamp(),
    lastModifiedBy: adminId,

    // Stats
    activePlayerCount:     0,
    totalEntriesSubmitted: 0,
    completedRaces:        0,
  });

  selectedRaces.forEach((race, index) => {
    const raceRef = doc(collection(slateRef, 'races'), race.id || `race_${index + 1}`);
    const prizePool = featureRaceOverrides?.[race.id] || defaultPrizePool;

    batch.set(raceRef, {
      id:              race.id || `race_${index + 1}`,
      slateId,
      raceNumber:      race.raceNumber || (index + 1),
      meetingName:     race.meetingName || '',
      meetingState:    race.meetingState || '',
      raceName:        race.raceName || `Race ${index + 1}`,
      raceClass:       race.raceClass || '',
      distance:        race.distance || null,
      scheduledJump:   race.scheduledJump ? new Date(race.scheduledJump) : null,
      externalRaceId:  race.externalRaceId || null,
      dataSource:      'admin_manual',
      prizePoolOverride: featureRaceOverrides?.[race.id] ? prizePool : null,

      status:           'pending',
      fieldSizeAtLock:  race.runners?.length || 0,
      activeRunnerCount: race.runners?.filter(r => r.status === 'active').length || 0,
      overroundAtLock:  null,
      lockedAt:         null,
      result:           null,
      isDeadHeat:       false,
      deadHeatPositions: null,
      adjustedPrizePool: null,
      hashManifestId:   null,
      resultsVerified:  false,
    });

    if (race.runners?.length > 0) {
      race.runners.forEach((runner) => {
        const runnerRef = doc(collection(raceRef, 'runners'), runner.id || `R${runner.runnerNumber}`);
        batch.set(runnerRef, {
          id:              runner.id || `R${runner.runnerNumber}`,
          raceId:          race.id,
          slateId,
          runnerNumber:    runner.runnerNumber,
          name:            runner.name || '',
          barrier:         runner.barrier || null,
          jockey:          runner.jockey || '',
          trainer:         runner.trainer || '',
          weight:          runner.weight || null,
          status:          runner.status || 'active',
          currentOdds:     runner.currentOdds || null,
          thursdayOdds:    null,  
          baseSharePrice:  null,  
          maxOwnershipPct: null,  
          isRaceFavourite: false,
          isJointFavourite: false,
          form:            runner.form || null,
        });
      });
    }
  });

  await batch.commit();
  return slateId;
}

export async function updateSlateDraft(slateId, updates, adminId) {
  const slateRef  = doc(db, 'game_slates', slateId);
  const slateSnap = await getDoc(slateRef);

  if (!slateSnap.exists()) throw new Error(`Slate ${slateId} not found`);
  if (slateSnap.data().pricesLocked) throw new Error('Cannot edit a slate after prices have been locked');

  await updateDoc(slateRef, {
    ...updates,
    lastModifiedAt: serverTimestamp(),
    lastModifiedBy: adminId,
  });
}

export async function fetchAdminDrafts(adminId) {
  const slatesRef = collection(db, 'game_slates');
  const snap = await getDocs(query(slatesRef, orderBy('createdAt', 'desc')));

  return snap.docs
    .filter(d => d.data().createdBy === adminId && d.data().status === 'building')
    .map(d => ({ id: d.id, ...d.data() }));
}